import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card/card';
import ComingSoonCover from '@/components/ComingSoonCover';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { Text } from '@/components/Typography/Text';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { MOCK_ONLY_FEATURE_FLAG } from '@/constants/featureFlags';
import { featureFlags } from '@/lib/env';
import placeholder from '../_placeholders/-assets/placeholder-operation-activity.png';
import { SummarySection } from './-components/SummarySection';
import { useActivityTable } from './-hooks/useTable';
import { useLicenseActivities } from './-query';
const ACTIVITY_TYPES = ['ライセンス付与', 'ライセンス剥奪', 'プラン変更', 'ライセンス割当申請'] as const;
export const Route = createFileRoute('/_staff/licenses/_list/operation-activity/')({
  component: LicenseOperationActivity
});
function LicenseOperationActivity() {
  const isMockEnabled = featureFlags.check(MOCK_ONLY_FEATURE_FLAG);
  const {
    data,
    isPending
  } = useLicenseActivities({
    enabled: isMockEnabled
  });
  const [keyword, setKeyword] = useState('');
  const [activityType, setActivityType] = useState('all');
  const [showSummary, setShowSummary] = useState(true);
  const rows = useMemo(() => {
    const items = data ?? [];
    const lower = keyword.trim().toLowerCase();
    return items.filter(item => {
      if (activityType !== 'all' && item.activityTypeLabel !== activityType) {
        return false;
      }
      if (!lower) return true;
      return [item.softwareName, item.licenseName, item.actorName].some(field => field.toLowerCase().includes(lower));
    });
  }, [data, keyword, activityType]);
  const {
    table,
    displayState
  } = useActivityTable(rows, isPending);

  // MOCK_ONLY 無効時は従来どおり ComingSoon（非破壊）
  if (!isMockEnabled) {
    return <ComingSoonCover placeholderImage={placeholder} />;
  }
  return <FillScreen className="flex flex-col gap-4 py-5">
      {showSummary && <SummarySection />}
      <VStack className="min-h-0 flex-1 gap-4 px-12">
        <Card className="p-2 shadow-xs">
          <HStack className="items-center justify-between">
            <HStack className="items-center gap-2">
              <SearchInput value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="ソフトウェア・ライセンス・実行者で検索" className="w-[280px]" />
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="操作で絞り込み" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての操作</SelectItem>
                  {ACTIVITY_TYPES.map(type => <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </HStack>
            <HStack className="items-center gap-2">
              <Switch checked={showSummary} onCheckedChange={setShowSummary} />
              <Text variant="pSmall" className="text-neutral-on-fill">
                サマリーを表示
              </Text>
            </HStack>
          </HStack>
        </Card>

        <Card className="min-h-0 flex-1 overflow-auto shadow-xs">
          <DataTable table={table} displayState={displayState} isHoverable onRowClick={null} />
        </Card>

        <DataTablePagination table={table} totalCount={rows.length} pageOptions={[20, 50, 100]} />
      </VStack>
    </FillScreen>;
}